import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { AppAlert, AppButton, AppModal } from '../../components/ui';
import { resolveAgendaMediaUrl } from '../../config/agendaApiConfig';
import agendaDatesService from '../../services/agendaDatesService';

const RATIOS = [
  [Number.NaN, 'Libre'],
  [1, '1:1'],
  [4 / 3, '4:3'],
  [3 / 2, '3:2'],
  [16 / 9, '16:9'],
];

const AgendaImageEditor = ({ show, path, src = '', fileName, canApply = true, onClose, onApply }) => {
  const imgRef = useRef(null);
  const cropperRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ratio, setRatio] = useState(Number.NaN);
  const [meta, setMeta] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!show) {
      setPreviewUrl('');
      setLoading(false);
      return undefined;
    }
    if (src) {
      setPreviewUrl(src);
      setLoading(false);
      return undefined;
    }
    const remote = resolveAgendaMediaUrl(path);
    if (!path) {
      setPreviewUrl('');
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';
    setLoading(true);
    setError('');
    setPreviewUrl('');
    agendaDatesService.downloadMedia(path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(remote || '');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [show, path, src]);

  useEffect(() => {
    setRatio(Number.NaN);
    setMeta({ x: 0, y: 0, width: 0, height: 0 });
    if (!show || !previewUrl) return undefined;

    let frame = 0;
    let stopped = false;

    const start = () => {
      const node = imgRef.current;
      const stage = node?.parentElement;
      if (stopped) return;
      if (node?.complete && node.naturalWidth < 1) {
        setError('Cette image ne peut pas être affichée.');
        return;
      }
      if (!node || !node.complete || node.naturalWidth < 1 || !stage || stage.clientWidth < 20) {
        frame = window.requestAnimationFrame(start);
        return;
      }
      cropperRef.current?.destroy();
      cropperRef.current = new Cropper(node, {
        viewMode: 1,
        autoCropArea: 1,
        responsive: true,
        background: false,
        checkCrossOrigin: false,
        checkOrientation: false,
        crop(event) {
          setMeta({
            x: Math.round(event.detail.x),
            y: Math.round(event.detail.y),
            width: Math.round(event.detail.width),
            height: Math.round(event.detail.height),
          });
        },
      });
    };

    const img = imgRef.current;
    if (img && !img.complete) img.addEventListener('load', start);
    frame = window.requestAnimationFrame(start);

    return () => {
      stopped = true;
      window.cancelAnimationFrame(frame);
      img?.removeEventListener('load', start);
      cropperRef.current?.destroy();
      cropperRef.current = null;
    };
  }, [show, previewUrl]);

  const withCropper = (action) => {
    const cropper = cropperRef.current;
    if (cropper) action(cropper);
  };

  const chooseRatio = (value) => {
    setRatio(value);
    withCropper((cropper) => cropper.setAspectRatio(value));
  };

  const download = async () => {
    const fallback = resolveAgendaMediaUrl(path);
    try {
      const blob = await agendaDatesService.downloadMedia(path);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'image';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      if (fallback) window.open(fallback, '_blank', 'noopener');
    }
  };

  const apply = async () => {
    const cropper = cropperRef.current;
    if (!cropper || !path) return;
    const data = cropper.getData();
    setSaving(true);
    setError('');
    try {
      const result = await agendaDatesService.cropMedia({
        path,
        x: Math.round(data.x),
        y: Math.round(data.y),
        width: Math.round(data.width),
        height: Math.round(data.height),
        rotate: Math.round(data.rotate || 0),
        scaleX: data.scaleX || 1,
        scaleY: data.scaleY || 1,
      });
      onApply(result.path || '');
    } catch (err) {
      setError(err.message || 'Recadrage impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      show={show}
      title="Recadrage de l’image"
      size="lg"
      className="agenda-dates"
      onClose={saving ? undefined : onClose}
      closeDisabled={saving}
      footer={(
        <>
          <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:download" iconWidth={16} disabled={!previewUrl || saving} onClick={download}>
            Télécharger
          </AppButton>
          <AppButton className="sw-btn sw-btn--page" variant="secondary" disabled={saving} onClick={onClose}>
            Annuler
          </AppButton>
          <AppButton className="sw-btn sw-btn--dark" variant="primary" loading={saving} loadingLabel="Recadrage…" disabled={!previewUrl || !canApply} onClick={apply}>
            Appliquer
          </AppButton>
        </>
      )}
    >
      {error ? <AppAlert variant="danger">{error}</AppAlert> : null}
      <div className="ag-crop-tools">
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:rotate-ccw" iconWidth={16} aria-label="Pivoter à gauche" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.rotate(-90))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:rotate-cw" iconWidth={16} aria-label="Pivoter à droite" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.rotate(90))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:flip-horizontal" iconWidth={16} aria-label="Miroir horizontal" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.scaleX(-(cropper.getData().scaleX || 1)))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:flip-vertical" iconWidth={16} aria-label="Miroir vertical" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.scaleY(-(cropper.getData().scaleY || 1)))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:zoom-in" iconWidth={16} aria-label="Zoom avant" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.zoom(0.1))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" icon="lucide:zoom-out" iconWidth={16} aria-label="Zoom arrière" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.zoom(-0.1))} />
        <AppButton className="sw-btn sw-btn--page" variant="secondary" disabled={!previewUrl || saving} onClick={() => withCropper((cropper) => cropper.reset())}>
          Réinitialiser
        </AppButton>
        {RATIOS.map(([value, label]) => (
          <button
            key={label}
            type="button"
            className={`ag-chip${(Number.isNaN(value) ? Number.isNaN(ratio) : ratio === value) ? ' is-on' : ''}`}
            disabled={!previewUrl || saving}
            onClick={() => chooseRatio(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="ag-crop-stage">
        {loading ? <p className="ag-note">Chargement de l’image…</p> : null}
        {previewUrl ? <img ref={imgRef} key={previewUrl} src={previewUrl} alt="" /> : null}
        {!loading && !previewUrl ? <p className="ag-note">Image introuvable.</p> : null}
      </div>
      <p className="ag-crop-meta">
        <span>X {meta.x}</span>
        <span>Y {meta.y}</span>
        <span>Largeur {meta.width}</span>
        <span>Hauteur {meta.height}</span>
      </p>
      <p className="ag-note">L’original est conservé. Appliquer crée une nouvelle image, à enregistrer ensuite sur la fiche.</p>
    </AppModal>
  );
};

export default AgendaImageEditor;
